// src/state/modalStore.js
// Centralized UI modal state (Zustand-only).
// We'll start with just one flag for the Recruiting modal.

import { create } from 'zustand';

export const useModalStore = create((set, get) => ({
  // --- Windows stacking (global z-order) ---
  // Order is from bottom -> top (last item is on top)
  windowOrder: ['chat', 'team', 'candidate', 'recruiting', 'designCenter', 'sharingCenter', 'helpCenter', 'profileAssistant'],
  baseZ: 20000,

  bringToFront: (key) =>
    set((s) => {
      const k = String(key || '').trim();
      if (!k) return {};
      const next = (s.windowOrder || []).filter((x) => x !== k);
      next.push(k);
      return { windowOrder: next };
    }),

  getZIndex: (key) => {
    const s = get();
    const k = String(key || '').trim();
    const order = Array.isArray(s.windowOrder) ? s.windowOrder : [];
    const idx = order.indexOf(k);

    // step spacing so future windows have room (tooltips, pickers, etc.)
    const step = 50;

    // If unknown key, keep it at the base
    if (idx === -1) return Number(s.baseZ);

    // Bottom window: baseZ + 50, next: +100, top: +150, etc.
    return Number(s.baseZ) + (idx + 1) * step;
  },


  // --- Modals ---
  recruitingOpen: false,

  designCenterOpen: false,

  sharingCenterOpen: false,
  sharingCenterPending: false,  // true while publish is in progress (shows throbber)
  sharingCenterModerationMsg: null, // string shown when moderation blocks a publish

  helpCenterOpen: false,

  teamOpen: false,
  teamId: null,

  candidateOpen: false,
  candidate: null,
  candidateViewerUId: null,
  candidateTop: 90,
  candidateRight: 30,
  candidateOffsetRight: 0,

  // --- Actions ---
  openRecruiting: () => set({ recruitingOpen: true }),
  closeRecruiting: () => set({ recruitingOpen: false }),
  toggleRecruiting: () => set((s) => ({ recruitingOpen: !s.recruitingOpen })),

  openDesignCenter: () => set({ designCenterOpen: true }),
  closeDesignCenter: () => set({ designCenterOpen: false }),
  toggleDesignCenter: () => set((s) => ({ designCenterOpen: !s.designCenterOpen })),

  openSharingCenter: (pending) => set({ sharingCenterOpen: true, sharingCenterPending: !!pending, sharingCenterModerationMsg: null }),
  closeSharingCenter: () => set({ sharingCenterOpen: false, sharingCenterPending: false, sharingCenterModerationMsg: null }),
  toggleSharingCenter: () => set((s) => ({ sharingCenterOpen: !s.sharingCenterOpen })),
  setSharingCenterPending: (v) => set({ sharingCenterPending: !!v }),
  setSharingCenterModerationMsg: (msg) => set({ sharingCenterModerationMsg: msg || null }),

  openHelpCenter: () => set({ helpCenterOpen: true }),
  closeHelpCenter: () => set({ helpCenterOpen: false, helpCenterPreSelect: null }),
  toggleHelpCenter: () => set((s) => ({ helpCenterOpen: !s.helpCenterOpen })),
  openHelpCenterTo: (section, data) => set({ helpCenterOpen: true, helpCenterPreSelect: { section, data } }),
  clearHelpCenterPreSelect: () => set({ helpCenterPreSelect: null }),

  openTeam: (teamId) => set({ teamOpen: true, teamId: Number(teamId) || null }),
  closeTeam: () => set({ teamOpen: false, teamId: null }),

  openCandidate: ({
    candidate,
    viewerUId,
    top = 90,
    right = 30,
    offsetRight = 0,
  }) =>
    set({
      candidateOpen: true,
      candidate: candidate || null,
      candidateViewerUId: Number(viewerUId) || null,
      candidateTop: Number(top) || 90,
      candidateRight: Number(right) || 30,
      candidateOffsetRight: Number(offsetRight) || 0,
    }),

  closeCandidate: () =>
    set({
      candidateOpen: false,
      candidate: null,
      candidateViewerUId: null,
      candidateTop: 90,
      candidateRight: 30,
      candidateOffsetRight: 0,
    }),

  // --- Walkthrough engine ---
  activeWalkthrough: null,   // walkthrough ID string (e.g. 'change-profile') or null
  walkthroughStep: 0,        // current step index
  walkthroughComplete: false, // true when user clicks Done on last step

  startWalkthrough: (id) =>
    set({ activeWalkthrough: id, walkthroughStep: 0, walkthroughComplete: false }),

  nextStep: () =>
    set((s) => ({ walkthroughStep: (s.walkthroughStep || 0) + 1 })),

  prevStep: () =>
    set((s) => ({ walkthroughStep: Math.max(0, (s.walkthroughStep || 0) - 1) })),

  resetWalkthrough: () =>
    set({ activeWalkthrough: null, walkthroughStep: 0, walkthroughComplete: false }),

  completeWalkthrough: () =>
    set({ walkthroughComplete: true, activeWalkthrough: null, walkthroughStep: 0 }),

  // Close every modal at once (logout, etc.)
  closeAll: () =>
    set({
      recruitingOpen: false,
      designCenterOpen: false,
      sharingCenterOpen: false,
      helpCenterOpen: false,
      sharingCenterPending: false,
      sharingCenterModerationMsg: null,
      teamOpen: false,
      teamId: null,
      candidateOpen: false,
      candidate: null,
      candidateViewerUId: null,
      candidateTop: 90,
      candidateRight: 30,
      candidateOffsetRight: 0,
      activeWalkthrough: null,
      walkthroughStep: 0,
      walkthroughComplete: false,
    }),
}));


